import { Injectable, computed, signal } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';

/** Accuracy threshold in metres — a fix at or below this is accepted immediately. */
export const ACCURACY_THRESHOLD_M = 50;

/**
 * How long to wait (ms) for a high-accuracy fix before falling back to any
 * available fix. Counts against the {@link MAX_TIMEOUT_MS} hard ceiling.
 */
export const ACCURACY_WAIT_MS = 10000;

/**
 * Hard ceiling (ms) for the entire acquisition attempt.
 * If no position is available after this duration, state transitions to `'failed'`.
 */
export const MAX_TIMEOUT_MS = 15000;

/**
 * GPS coordinates captured from the device sensor.
 */
export interface GpsCoordinates {
  /** Decimal degrees latitude. */
  latitude: number;
  /** Decimal degrees longitude. */
  longitude: number;
  /** Estimated horizontal accuracy in metres (lower = more precise). */
  accuracy: number;
}

/**
 * Discriminated union describing every lifecycle state of a GPS acquisition:
 * - `'idle'`        — acquisition not yet started
 * - `'acquiring'`   — in progress; awaiting a position fix
 * - `GpsCoordinates` — success; the state value IS the coordinates object
 * - `'failed'`      — permission denied, timed out, or plugin unavailable
 *
 * @remarks
 * Use `typeof state === 'object'` to distinguish the success branch from
 * the string states — this avoids an `instanceof` check and works with
 * Angular's `computed()` without extra imports.
 */
export type GpsState = 'idle' | 'acquiring' | GpsCoordinates | 'failed';

/**
 * GPS acquisition service.
 *
 * @remarks
 * Wraps `@capacitor/geolocation` with a two-attempt strategy and
 * an Angular Signals-based lifecycle:
 *
 * 1. Requests permission via `Geolocation.requestPermissions()`.
 * 2. Tries a high-accuracy fix for up to {@link ACCURACY_WAIT_MS}.
 *    Accepts immediately if accuracy ≤ {@link ACCURACY_THRESHOLD_M} m.
 * 3. If the first fix exceeds the threshold, makes one more attempt
 *    (any accuracy) for the remaining `MAX_TIMEOUT_MS − ACCURACY_WAIT_MS`.
 * 4. Accepts the best available fix; transitions to `'failed'` only when
 *    both attempts throw (permission denied, device unavailable, etc.).
 *
 * **HTTPS required** — browsers block `navigator.geolocation` on non-secure
 * origins. Errors are logged to `console.warn` rather than swallowed silently.
 *
 * @example
 * ```ts
 * const gps = inject(GpsService);
 * await gps.startAcquisition();
 * if (gps.isCaptured()) {
 *   const { latitude, longitude } = gps.coordinates()!;
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class GpsService {
  /**
   * Reactive GPS lifecycle state.
   * Transitions: `'idle'` → `'acquiring'` → `GpsCoordinates` | `'failed'`.
   * Bind templates directly to `gpsState()` for amber/green/red pill rendering.
   */
  readonly gpsState = signal<GpsState>('idle');

  /**
   * `true` when the user has permanently denied location access and the OS
   * will no longer show the permission dialog.  Set by {@link ensurePermission};
   * reset to `false` when permission is granted.
   *
   * Consumers (e.g. `DashboardComponent`) watch this signal to show a blocking
   * "enable location" prompt without polling the OS on every render.
   */
  readonly permissionDenied = signal(false);

  /**
   * `true` when a coordinate fix has been captured.
   * Use this to conditionally show the green pill or enable form submission.
   */
  readonly isCaptured = computed(() => typeof this.gpsState() === 'object');

  /**
   * The captured coordinates, or `null` while idle, acquiring, or failed.
   * Convenience accessor for building API payloads — avoids a type-narrowing
   * check on every call site.
   */
  readonly coordinates = computed<GpsCoordinates | null>(() => {
    const s = this.gpsState();
    return typeof s === 'object' ? s : null;
  });

  /**
   * Checks the current location permission state and requests it if not yet
   * determined.
   *
   * @remarks
   * Call this on native platforms at app startup (after login) so the Android
   * OS dialog appears before the user tries to capture a photo.  Android can
   * suppress the runtime permission dialog when it is first requested mid-flow
   * inside a camera capture, resulting in a silent `denied` response.
   *
   * State mapping:
   * - `'granted'`              → returns `true` immediately (no dialog)
   * - `'denied'`               → returns `false` (permanently denied; OS will
   *                               not show the dialog again)
   * - `'prompt'` / `'prompt-with-rationale'` → shows the OS dialog; returns
   *                               `true` if the user grants, `false` otherwise
   *
   * On web or non-HTTPS origins `checkPermissions` may throw — the catch block
   * returns `true` so the browser shows its own prompt on the first
   * `getCurrentPosition` call.
   *
   * @returns `true` when location access is available or was just granted;
   *          `false` when permanently denied.
   */
  async ensurePermission(): Promise<boolean> {
    try {
      const { location } = await Geolocation.checkPermissions();
      if (location === 'granted') { this.permissionDenied.set(false); return true; }
      if (location === 'denied')  { this.permissionDenied.set(true);  return false; }
      const { location: result } = await Geolocation.requestPermissions();
      const granted = result !== 'denied';
      this.permissionDenied.set(!granted);
      return granted;
    } catch {
      this.permissionDenied.set(false);
      return true; // web / non-HTTPS fallback — browser handles its own prompt
    }
  }

  /**
   * Starts GPS acquisition.
   *
   * Transitions to `'acquiring'` synchronously on the calling tick, then
   * resolves asynchronously to either a {@link GpsCoordinates} object or
   * `'failed'`.  Safe to call again from a retry tap on the red pill.
   */
  async startAcquisition(): Promise<void> {
    this.gpsState.set('acquiring');
    const granted = await this.requestPermission();
    if (!granted) { this.gpsState.set('failed'); return; }
    const coords = await this.acquireCoordinates();
    this.gpsState.set(coords ?? 'failed');
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  /**
   * Requests device location permission.
   * Returns `true` unless explicitly denied — on web, or when the plugin
   * throws (e.g. non-HTTPS origin), we proceed and let `getCurrentPosition`
   * surface the browser's own permission prompt.
   */
  private async requestPermission(): Promise<boolean> {
    try {
      const perm = await Geolocation.requestPermissions();
      return perm.location !== 'denied';
    } catch {
      return true;
    }
  }

  /**
   * Two-attempt acquisition strategy:
   * 1. High-accuracy mode, up to {@link ACCURACY_WAIT_MS} — accepted
   *    immediately if accuracy ≤ {@link ACCURACY_THRESHOLD_M} m.
   * 2. Any-accuracy mode, up to `MAX_TIMEOUT_MS − ACCURACY_WAIT_MS` —
   *    accepts whatever arrives; falls back to the first fix if this attempt
   *    throws.
   *
   * Returns `null` only when both attempts fail (triggers `'failed'` state).
   */
  private async acquireCoordinates(): Promise<GpsCoordinates | null> {
    const precise = await this.tryGetPosition({ enableHighAccuracy: true, timeout: ACCURACY_WAIT_MS });
    if (precise && precise.accuracy <= ACCURACY_THRESHOLD_M) return precise;

    const remaining = await this.tryGetPosition({
      enableHighAccuracy: false,
      timeout: MAX_TIMEOUT_MS - ACCURACY_WAIT_MS,
    });
    return remaining ?? precise ?? null;
  }

  /**
   * Wraps `Geolocation.getCurrentPosition` with error handling.
   * Any error (permission, timeout, HTTPS, plugin unavailable) is logged
   * and mapped to `null` so the caller can apply its fallback strategy.
   *
   * @param opts - Options forwarded to `Geolocation.getCurrentPosition`.
   */
  private async tryGetPosition(
    opts: { enableHighAccuracy: boolean; timeout: number },
  ): Promise<GpsCoordinates | null> {
    try {
      const pos = await Geolocation.getCurrentPosition(opts);
      return {
        latitude:  pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy:  pos.coords.accuracy ?? 0,
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[GpsService] Position acquisition failed:', msg);
      return null;
    }
  }
}
